import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useCompanyNetworkState,
  type CompanyNetworkFirma,
} from "../../services/companyNetworkService";

/**
 * Scope „Dieses Unternehmen / Unternehmensverbund" für Kontakt-Reiter: Ebene, Firmenliste und
 * Firmen-Auswahl an einer Stelle, damit Kennzahlen, Risiken & Co. denselben
 * Scope-Begriff (und dieselbe Leiste, siehe VerbundScopeBar.tsx) teilen.
 * Logik-Vorbild ist die Vermögens-Matrix (components/contactRisks/VermoegensMatrix.tsx).
 *
 * Die frühere dritte Ebene „Kundenverbindung" ist bewusst nicht dabei —
 * Betriebsvermögen und Kennzahlen werden je Rechtsträger bzw. Verbund
 * betrachtet, nicht über die Kundenverbindung hinweg (Meeting 24.07.2026).
 */

export type VerbundEbene = "firma" | "verbund";

export const VERBUND_EBENEN: { id: VerbundEbene; label: string; hint: string }[] = [
  { id: "firma", label: "Dieses Unternehmen", hint: "Nur der geöffnete Kontakt." },
  {
    id: "verbund",
    label: "Unternehmensverbund",
    hint: "Alle über Tochter-/Schwester-/Beteiligungs-Verbindungen erreichbaren Firmen.",
  },
];

/**
 * Ab so vielen Firmen im Scope wird die Chip-Reihe unbrauchbar (sie wächst
 * über mehrere Zeilen). `getCompanyNetwork` hat — anders als der
 * Graph-Endpoint (80 Firmen/30 Personen) — KEIN Limit, große Konzerne kommen
 * also vollständig an; die Leiste schaltet dann auf eine kompakte
 * Mehrfachauswahl um.
 */
const CHIP_LIMIT = 25;

export interface VerbundScope {
  ebene: VerbundEbene;
  setEbene: (ebene: VerbundEbene) => void;
  /** Firmen der aktuellen Ebene — die geöffnete Firma immer zuerst. */
  scopeFirmen: CompanyNetworkFirma[];
  selected: Set<number>;
  toggleFirm: (id: number) => void;
  /** Aufsteigend sortiert — über `join(",")` ein stabiler Fetch-Key. */
  selectedIds: number[];
  /** Mehr als eine Firma gewählt → angezeigte Werte sind konsolidiert. */
  isConsolidated: boolean;
  /** Name der einzigen gewählten Firma (nur wenn nicht konsolidiert). */
  singleName?: string;
  networkLoading: boolean;
  /** Verbund zu groß für Chips → kompakte Mehrfachauswahl. */
  tooManyFirms: boolean;
  /** Obergrenze der gleichzeitigen Auswahl (`undefined` = keine). */
  maxSelected?: number;
  /** Der Verbund ist größer als `maxSelected` — es kann nie alles gewählt sein. */
  selectionCapped: boolean;
}

/**
 * Führt `weiter` aus, sobald `frage` zustimmt — synchron bei boolean, nach dem
 * Auflösen bei einem Promise (Rückfrage-Popup). Ohne `frage` sofort.
 */
function nachFreigabe(frage: (() => boolean | Promise<boolean>) | undefined, weiter: () => void): void {
  const antwort = frage?.();
  if (antwort === false) return;
  if (antwort && typeof antwort === "object" && typeof antwort.then === "function") {
    void antwort.then((ok) => {
      if (ok) weiter();
    });
    return;
  }
  weiter();
}

export interface UseVerbundScopeOptions {
  /**
   * Wird vor jeder Scope-Änderung gefragt; liefert sie `false`, bleibt der
   * Scope stehen — für die Rückfrage bei ungespeicherten Änderungen. Ein
   * Promise (eigenes Rückfrage-Popup) wird abgewartet.
   */
  onBeforeChange?: () => boolean | Promise<boolean>;
  /**
   * Obergrenze der gleichzeitigen Auswahl. Reiter, deren Batch-Endpoint einen
   * Deckel hat, geben ihn hier mit: sonst schickt die Ansicht 60 Ids, bekommt
   * die Werte von 50 zurück und zeigt eine Teilsumme, die wie die Gesamtsumme
   * aussieht — die Abdeckungszeile („N von 60 Firmen") bedeutet ja „so viele
   * haben Daten erfasst", nicht „so viele wurden geladen".
   */
  maxSelected?: number;
}

export function useVerbundScope(
  contactId: number,
  contactName?: string | null,
  opts?: UseVerbundScopeOptions,
): VerbundScope {
  const { firmen: network, loading: networkLoading } = useCompanyNetworkState(contactId);
  const maxSelected = opts?.maxSelected;
  const limit = maxSelected ?? Number.POSITIVE_INFINITY;

  const [ebene, setEbeneState] = useState<VerbundEbene>("firma");
  const [selected, setSelected] = useState<Set<number>>(() => new Set([contactId]));

  // Callback über eine Ref halten, damit setEbene/toggleFirm stabil bleiben,
  // auch wenn der Aufrufer die Funktion bei jedem Render neu erzeugt.
  const onBeforeChangeRef = useRef(opts?.onBeforeChange);
  onBeforeChangeRef.current = opts?.onBeforeChange;

  // Firmenliste je Ebene — geöffnete Firma immer zuerst, Rest dedupliziert
  const scopeFirmen = useMemo<CompanyNetworkFirma[]>(() => {
    const self: CompanyNetworkFirma = {
      id: contactId,
      name: contactName ?? network.find((f) => f.id === contactId)?.name ?? `Kontakt ${contactId}`,
    };
    if (ebene === "firma") return [self];
    const map = new Map<number, CompanyNetworkFirma>();
    map.set(self.id, self);
    for (const f of network) if (!map.has(f.id)) map.set(f.id, f);
    return Array.from(map.values());
  }, [ebene, contactId, contactName, network]);

  // Ebenen-Wechsel oder neue Firmenliste → alle Firmen der Ebene auswählen
  const scopeKey = useMemo(
    () => `${ebene}|${scopeFirmen.map((f) => f.id).join(",")}`,
    [ebene, scopeFirmen],
  );
  useEffect(() => {
    setSelected(new Set(scopeFirmen.slice(0, limit).map((f) => f.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, limit]);

  // Kontaktwechsel: zurück auf „Dieses Unternehmen" und Auswahl auf den neuen Kontakt.
  // Beides MUSS in diesem einen, auf contactId gekeyten Effect passieren (und
  // nach dem scopeKey-Effect, damit es gewinnt): `useCompanyNetworkState` leert
  // die Liste beim Kontaktwechsel sofort auf []. Läuft der Reset in einem anderen
  // Effect, hält `selected` für einen Render die Ids des vorherigen Verbunds
  // und löst einen Fetch auf fremde Kontakte aus.
  useEffect(() => {
    setEbeneState("firma");
    setSelected(new Set([contactId]));
  }, [contactId]);

  const selectedIds = useMemo(
    () =>
      scopeFirmen
        .map((f) => f.id)
        .filter((id) => selected.has(id))
        .sort((a, b) => a - b),
    [scopeFirmen, selected],
  );

  // Rückfrage bewusst VOR setState — nicht im State-Updater, den React im
  // StrictMode doppelt ausführt (der Dialog käme zweimal).
  const setEbene = useCallback(
    (next: VerbundEbene) => {
      if (next === ebene) return;
      nachFreigabe(onBeforeChangeRef.current, () => setEbeneState(next));
    },
    [ebene],
  );

  const toggleFirm = useCallback(
    (id: number) => {
      nachFreigabe(onBeforeChangeRef.current, () => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        // Über die Grenze hinaus lässt sich nichts dazuwählen: der Batch-Endpoint
        // würde die überzähligen Ids still verwerfen und die Summe wäre wieder
        // eine Teilsumme, die vollständig aussieht.
        else if (next.size < limit) next.add(id);
        // Eine leere Auswahl gibt es nicht — sonst stünde der Reiter ohne Bezug da
        if (next.size === 0) next.add(contactId);
        return next;
      }));
    },
    [contactId, limit],
  );

  const isConsolidated = selectedIds.length > 1;
  const singleName = isConsolidated
    ? undefined
    : (scopeFirmen.find((f) => f.id === selectedIds[0])?.name ?? undefined);

  return {
    ebene,
    setEbene,
    scopeFirmen,
    selected,
    toggleFirm,
    selectedIds,
    isConsolidated,
    singleName,
    networkLoading,
    tooManyFirms: scopeFirmen.length > CHIP_LIMIT,
    maxSelected,
    selectionCapped: maxSelected !== undefined && scopeFirmen.length > maxSelected,
  };
}
